import { CardMetric } from '../types';
import { ReversalAggregateBase, ReversalResult } from './reversalResult';

/**
 * PLA-8828: the three bespoke Reversal shapes as ONE tile's reading.
 *
 * `reversalResult.ts` parses the wire and `api/customerInsights` has been attaching the result to
 * every fetch since PLA-8825 -- with nothing on the other end reading it. This is the other end, and
 * it exists for the same reason `productReadinessMetric.ts` does: `C-REV-002/003/004` carry no
 * `metric_value` and no `calculation_status`, so read through the standard row every one of them
 * becomes a reading of `null` wearing a manufactured empty status.
 *
 * UNVERIFIED end to end. All three answered ZERO rows for customer 979417 on `api-uat.perxtech.io`,
 * so nothing below has run against a populated row.
 */

/** A total, or `null` where not one row carried the column -- never a zero standing in for one. */
const sumOf = (values: (number | null)[]): number | null => {
  const numbers = values.filter(value => typeof value === 'number');

  return numbers.length ? numbers.reduce((total, value) => total + value, 0) : null;
};

/** The first row of whichever shape answered, for the governed columns all three share. */
const firstRowOf = (result: ReversalResult) => {
  if (result.shape === 'events') {
    return result.rows[0];
  }

  const aggregates: ReversalAggregateBase[] = result.rows;

  return aggregates[0];
};

/**
 * The amount reversed, across however many rows the shape returned: `reversed_amount` per event, or
 * `total_reversed_amount` per group.
 */
const reversedAmount = (result: ReversalResult): number | null => {
  if (result.shape === 'events') {
    return sumOf(result.rows.map(row => row.reversed_amount));
  }

  // Widened to the shared base rather than mapped over `result.rows` directly: the two aggregate
  // arms are still a union of two ARRAY types here, and `.map` on one of those does not resolve.
  const aggregates: ReversalAggregateBase[] = result.rows;

  return sumOf(aggregates.map(row => row.total_reversed_amount));
};

/**
 * One Reversal tile.
 *
 * A reading is resolved for a `currency` tile ONLY. The sum of a reversed-amount column is the value
 * reversed and nothing else, so that one is a reading rather than a derivation -- but the other two
 * tiles pointed at these metrics are a `percent` ("Reversal Rate") and a `points` ("Reversed
 * Outcome"), and no column of any of the three shapes states either. They keep `value: null`, which
 * draws the same empty tile they draw today; the rows themselves reach the reader as the card's
 * evidence (`evidenceRows.ts`), which is where a breakdown belongs.
 *
 * Inventing a quotient here to fill the percent tile is the one thing this file must not do: a
 * fabricated rate renders perfectly and is indistinguishable from a measured one.
 */
export const reversalCardMetric = (
  base: Pick<CardMetric, 'key' | 'label' | 'format'>,
  result: ReversalResult
): CardMetric => {
  const row = firstRowOf(result);
  const expression = row?.governed_formula_expression;
  const version = row?.governed_formula_version;

  return {
    ...base,
    value: base.format === 'currency' ? reversedAmount(result) : null,
    // `formulaExpression`, not `formula`: the tile's own caption is authored, never the wire's (PLA-8815).
    ...(expression ? { formulaExpression: expression } : {}),
    ...(version ? { formulaVersion: version } : {})
  };
};

export default reversalCardMetric;
